import { useState, useEffect } from 'react';
import { getPaymentStats } from '../api/payments';
import { getCustomers } from '../api/customers';
import { getPayments } from '../api/payments';

/**
 * Custom hook to fetch and combine dashboard data
 * @returns {Object} Dashboard data and loading/error states
 */
const useDashboardData = () => {
  const [stats, setStats] = useState({
    totalCustomers: 0,
    pendingPayments: 0,
    confirmedPayments: 0,
    totalAmount: 0
  });
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch all data in parallel - including total customer count
        const [statsResponse, customersResponse, paymentsResponse, customerCountResponse] = await Promise.all([
          getPaymentStats(),
          getCustomers({ limit: 5, sort: '-createdAt' }),
          getPayments({ limit: 5, sort: '-createdAt' }),
          getCustomers({ limit: 1 }) // We only need the total count from this
        ]);
        
        // Process the stats from the backend format
        const pendingPaymentsCount = statsResponse.statuses?.find(s => s._id === 'pending')?.count || 0;
        const confirmedPaymentsCount = statsResponse.statuses?.find(s => s._id === 'confirmed')?.count || 0;
        
        // Backend returns customers with a 'customers' field and pagination info
        const totalCustomersCount = customerCountResponse.pagination?.total || 0;
        
        // Update stats in the format expected by the UI components
        setStats({
          totalCustomers: totalCustomersCount,
          pendingPayments: pendingPaymentsCount,
          confirmedPayments: confirmedPaymentsCount,
          totalAmount: statsResponse.totalRevenue || 0
        });
        
        // Extract customers from the backend response (backend returns { customers: [...] })
        setRecentCustomers(customersResponse.customers || []);
        
        // Extract payments from the backend response (backend returns { payments: [...] })
        setRecentPayments(paymentsResponse.payments || []);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  return {
    stats,
    recentCustomers,
    recentPayments,
    loading,
    error,
    refreshData: () => {
      // Function to manually refresh data if needed
      setLoading(true);
      // Re-run the effect to fetch fresh data
      // React will re-run the effect when loading changes
    }
  };
};

export default useDashboardData;